import BaseComponent from './BaseComponent';
import ParentMenu from './ParentMenu';
import MenuLayers from './MenuLayers';
import { gsap } from 'gsap';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { getKeyboardFocusableElements, trapFocus } from '../utils';
gsap.registerPlugin(ScrollTrigger);

export default class Menu extends BaseComponent {
	constructor(elm, header) {
		super();

		this.header = header;
		this.breakpoint = window.matchMedia('(min-width: 992px)');
		this.reduceMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
		this.isOpen = false;

		// Elements
		this.elm = elm;
		this.menuWrapper = this.header.elm.querySelector('[data-menu-wrapper]');
		this.background = this.header.elm.querySelector('[data-menu-background]');
		this.backgroundDesktop = this.header.elm.querySelector('[data-menu-background-desktop]');
		this.vacancyButton = this.header.elm.querySelector('[data-vacancy-cta]');
		this.links = this.elm.querySelectorAll('a, button');

		// Parent menu items
		this.parentMenuItems = this.elm.querySelectorAll('[data-menu-parent]');
		this.parentMenus = [];
		this.parentMenuItems.forEach((parentMenuItem) => {
			this.parentMenus.push(new ParentMenu(parentMenuItem, this));
		});
		this.closeBackgroundTimer = null;

		// Layer menu
		this.menuLayers = new MenuLayers(this.header, this);

		this.setInitialState();

		// Listeners
		this.breakpoint.addEventListener('change', () => {
			this.setInitialState();
		});

		this.headerLogo = this.header.elm.querySelector('[data-header-logo]');
		this.headerLogo.addEventListener('click', (e) => {
			this.cleanupLinks();
		});

		if (this.breakpoint.matches) {
			this.links.forEach((link) => {
				link.addEventListener('click', (e) => {
					this.cleanupLinks();
					if (link.tagName.toLowerCase() !== 'button') {
						link.closest('.c-menu__item').classList.add('is-active');
					}
				});
			});
		}

		this.blessHandler = (e) => {
			const container = e.target;
			const hero = container.querySelector('[data-hero]');

			if (this.headerTrigger) {
				this.headerTrigger.kill();
			}

			if (this.scrollTrigger) {
				this.scrollTrigger.kill();
			}

			this.header.elm.classList.remove('scrolled-past-hero');
			this.header.elm.classList.remove('has-scrolled');

			this.headerTrigger = new ScrollTrigger({
				trigger: hero,
				start: 'bottom top',
				onEnterBack: () => {
					this.header.elm.classList.remove('scrolled-past-hero');
				},
				onLeave: () => {
					this.header.elm.classList.add('scrolled-past-hero');
				},
			});

			this.scrollTrigger = ScrollTrigger.create({
				start: 'top+=20 top',
				onEnter: () => {
					this.header.elm.classList.add('has-scrolled');
					if (!this.header.elm.classList.contains('is-open')) {
						this.animateDesktopBackground(100);
					}
				},
				onLeaveBack: () => {
					this.header.elm.classList.remove('has-scrolled');
					if (!this.header.elm.classList.contains('is-open')) {
						this.animateDesktopBackground(0);
					}
				},
			});

			this.animateDesktopBackground(0);
		};

		document.addEventListener('bless', this.blessHandler);

		document.addEventListener('leave', () => {
			this.links.forEach((link) => {
				link.closest('.c-menu__item').classList.remove('is-active');
			});
		});

		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape') {
				this.close();
			}
		});

		this.header.elm.addEventListener('keydown', (e) => {
			if (e.key !== 'Tab') {
				return;
			}

			if (!this.isOpen) {
				return;
			}

			const focusableElements = [
				this.header.menuButton,
				...getKeyboardFocusableElements(this.elm),
				this.vacancyButton,
			];

			if (focusableElements.length === 0) {
				return;
			}

			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];
			const currentElement = document.activeElement;

			if (e.shiftKey) {
				if (currentElement === firstElement) {
					e.preventDefault();
					lastElement.focus();
				}
			} else {
				if (currentElement === lastElement) {
					e.preventDefault();
					firstElement.focus();
				}
			}
		});
	}

	setInitialState() {
		gsap.set(this.background, {
			transformOrigin: 'top',
			zIndex: -1,
			scaleY: 0,
		});

		gsap.set(this.elm, {
			autoAlpha: 0,
		});

		if (this.breakpoint.matches) {
			gsap.set(this.menuWrapper, {
				autoAlpha: 1,
				opacity: 1,
			});

			gsap.set(this.backgroundDesktop, {
				display: 'block',
				delay: 0.3,
			});
		} else {
			gsap.set(this.menuWrapper, {
				autoAlpha: 0,
				opacity: 0,
			});

			gsap.set(this.backgroundDesktop, {
				display: 'none',
			});
		}

		if (this.breakpoint.matches) {
			this.header.elm.classList.remove('is-open');
			this.isOpen = false;
			this.header.menuButton.querySelector('span').innerHTML = 'Menu openen';
			this.header.menuButton.setAttribute('aria-expanded', 'false');
			this.links.forEach((link) => {
				link.closest('.c-menu__item').classList.remove('is-active');
			});
			enablePageScroll(this.elm);
		}

		gsap.to(this.elm, {
			autoAlpha: 1,
			duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
			ease: 'sine.out',
		});
	}

	cleanupLinks() {
		this.links.forEach((link) => {
			link.closest('.c-menu__item').classList.remove('is-active');
		});
	}

	toggle() {
		if (this.isOpen) {
			this.close();
		} else {
			this.open();
		}
	}

	open() {
		if (!this.breakpoint.matches) {
			this.header.elm.classList.add('is-open');
			this.isOpen = true;
			this.header.menuButton.querySelector('span').innerHTML = 'Menu sluiten';
			this.header.menuButton.setAttribute('aria-expanded', 'true');

			const tl = gsap.timeline();

			tl.to(this.background, {
				scaleY: 1,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
				ease: 'sine.out',
			});

			tl.to(this.menuWrapper, {
				autoAlpha: 1,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
				ease: 'sine.out',
			});

			disablePageScroll(this.elm);
		}
	}

	close() {
		this.parentMenus.forEach((parentMenu) => {
			parentMenu.close();
		});

		this.menuLayers.closeSubMenu(this.menuLayers.activeMenuItem);

		if (!this.breakpoint.matches) {
			const tl = gsap.timeline();

			tl.to(this.menuWrapper, {
				autoAlpha: 0,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
				ease: 'sine.in',
			});

			tl.to(this.background, {
				scaleY: 0,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
				ease: 'sine.in',
			});

			enablePageScroll(this.elm);
		}

		this.header.elm.classList.remove('is-open');
		this.isOpen = false;
		this.header.menuButton.querySelector('span').innerHTML = 'Menu openen';
		this.header.menuButton.setAttribute('aria-expanded', 'false');
	}

	closeOtherParentMenus(currentParentMenu) {
		const filteredParentMenus = this.parentMenus.filter((parentMenu) => parentMenu !== currentParentMenu);
		filteredParentMenus.forEach((parentMenu) => {
			parentMenu.close();
		});
	}

	animateDesktopBackground(height) {
		if (height === 0) {
			if (
				document.body.classList.contains('header--transparent') &&
				!this.header.elm.classList.contains('has-scrolled')
			) {
				height = 0;
			} else {
				height = 100;
			}
		}

		gsap.to(this.backgroundDesktop, {
			clipPath: `inset(0 0 ${window.innerHeight - height}px 0)`,
			duration: this.reduceMotionMediaQuery.matches ? 0 : 0.3,
			ease: 'power2.out',
		});
	}
}
